import { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Seo, { stripHtml } from "@/components/Seo";
import { Clock, MapPin, ArrowRight, Compass, Users, Calendar as CalIcon, Video, Star, SlidersHorizontal, Search, Lock, Home } from "lucide-react";
import BookingModal from "@/components/BookingModal";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LocationBadge } from "@/components/LocationBadge";
import { money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";

const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/34daee87c523c418e6125459751141d73f7319056b318acddc7315c6ba00c78a.png";
const FALLBACK = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const flowSteps = [
  { n: "01", label: "Offering", icon: Compass },
  { n: "02", label: "Location", icon: MapPin },
  { n: "03", label: "Enhancer", icon: Users },
  { n: "04", label: "Time", icon: CalIcon },
];

// Homepage need-card themes → keywords matched against offering title/category/description.
const NEED_THEMES = {
  rest: { label: "Rest & Restoration", keywords: ["float", "sauna", "steam", "red light", "massage", "relax", "rest", "sleep", "restor"] },
  emotional: { label: "Emotional Clarity & Balance", keywords: ["reiki", "energy", "emotional", "healing", "heart", "grief", "support"] },
  body: { label: "Somatic Grounding & Vitality", keywords: ["breathwork", "massage", "somatic", "movement", "yoga", "ionic", "foot", "body", "nervous system"] },
  presence: { label: "Mindful Presence & Focus", keywords: ["meditation", "mindful", "sound", "presence", "stillness", "focus", "breath"] },
  purpose: { label: "Purposeful Self-Discovery", keywords: ["coaching", "mentorship", "guidance", "gauge", "purpose", "discovery", "recovery"] },
  relational: { label: "Relational Harmony", keywords: ["couple", "relationship", "circle", "communication", "boundar", "connection"] },
};

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-4 py-2 text-sm transition-colors ${active ? "bg-[#144F75] text-white" : "bg-white border border-[#144F75]/10 text-[#4A5D6B] hover:border-[#144F75]/40"}`}>
    {children}
  </button>
);

export default function WellnessServices() {
  const [selected, setSelected] = useState(null);
  const { id: routeId } = useParams();
  const nav = useNavigate();
  const [loc, setLoc] = useState("all");
  const [enh, setEnh] = useState("all");
  const [cat, setCat] = useState("all");
  const [focus, setFocus] = useState("all");
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const needs = (searchParams.get("needs") || "").split(",").filter((n) => NEED_THEMES[n]);
  const removeNeed = (id) => {
    const next = needs.filter((n) => n !== id);
    if (next.length) searchParams.set("needs", next.join(","));
    else searchParams.delete("needs");
    setSearchParams(searchParams, { replace: true });
  };
  const clearNeeds = () => { searchParams.delete("needs"); setSearchParams(searchParams, { replace: true }); };
  const { user } = useAuth();
  const isMember = user && ["community", "enhancer", "staff", "council", "admin"].includes(user.role);

  const { data, isLoading } = useQuery({ queryKey: ["discovery-offerings"], queryFn: () => api.get("/discovery/offerings").then((r) => r.data), refetchInterval: 60000 });
  const { data: packages = [] } = useQuery({ queryKey: ["packages-catalog"], queryFn: () => api.get("/packages/catalog").then((r) => r.data) });
  const offerings = data?.offerings || [];
  const rawFacets = data?.facets || { locations: [], enhancers: [], categories: [], focus_areas: [] };
  const { data: home } = useQuery({ queryKey: ["my-home-center"], queryFn: () => api.get("/me/home-center").then((r) => r.data), enabled: !!user });
  const homeId = home?.location_id || "";
  const facets = useMemo(() => (!homeId ? rawFacets : { ...rawFacets, locations: [...rawFacets.locations].sort((a, b) => (b.id === homeId) - (a.id === homeId)) }), [rawFacets, homeId]); // eslint-disable-line

  // Deep link: /offerings/:id (or /wellness/:id) auto-opens the offering.
  useEffect(() => {
    if (routeId && offerings.length) {
      const o = offerings.find((x) => x.id === routeId || (x.slug && x.slug === routeId));
      if (o) setSelected(o);
    }
  }, [routeId, offerings]);

  const closeModal = () => { setSelected(null); if (routeId) nav("/offerings"); };

  const matchEnh = (o) => enh === "all" || o.enhancers?.some((e) => e.id === enh);
  const matchCat = (o) => cat === "all" || o.category === cat;
  const matchLoc = (o) => loc === "all" || (loc === "virtual" ? o.virtual : o.centers?.some((c) => c.id === loc));
  const matchFocus = (o) => focus === "all" || (o.focus_areas || []).includes(focus);
  const matchQ = (o) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [o.title, o.short_description, o.category, ...(o.focus_areas || []), ...(o.enhancers || []).map((e) => e.name)]
      .filter(Boolean).join(" ").toLowerCase().includes(s);
  };
  const matchNeeds = (o) => {
    if (!needs.length) return true;
    const text = [o.title, o.short_description, o.description, o.category, ...(o.focus_areas || [])].filter(Boolean).join(" ").toLowerCase();
    return needs.some((id) => NEED_THEMES[id].keywords.some((k) => text.includes(k)));
  };
  const allMatch = (o) => matchLoc(o) && matchEnh(o) && matchCat(o) && matchFocus(o) && matchQ(o) && matchNeeds(o);

  // A location filter shows that location's own price when it differs.
  const filtered = useMemo(() => offerings.filter(allMatch).map((o) => (loc !== "all" && o.location_prices?.[loc] != null ? { ...o, price: o.location_prices[loc] } : o)),
    [offerings, loc, enh, cat, focus, q, needs.join(",")]); // eslint-disable-line

  // Grouped sections when no specific location is chosen.
  const groups = useMemo(() => {
    if (loc !== "all") return null;
    const secs = [];
    for (const l of facets.locations) {
      const list = offerings.filter((o) => matchEnh(o) && matchCat(o) && matchFocus(o) && matchQ(o) && matchNeeds(o) && (l.kind === "virtual" ? o.virtual : o.centers?.some((c) => c.id === l.id)));
      if (list.length) secs.push({ location: l, list });
    }
    return secs;
  }, [offerings, facets, loc, enh, cat, focus, q, needs.join(",")]); // eslint-disable-line

  const anyFilter = loc !== "all" || enh !== "all" || cat !== "all" || focus !== "all" || q.trim() || needs.length > 0;
  const resultCount = filtered.length;

  return (
    <div data-testid="wellness-page" className="bg-[#F9F7F3]">
      <section className="relative py-24 md:py-28 overflow-hidden" data-testid="wellness-hero">
        <img src={HERO} alt="A restorative wellness session in warm light" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F3A56]/95 via-[#0F3A56]/70 to-[#144F75]/25" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D98E73] mb-4">Holistic Recovery & Wellness</p>
            <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white tracking-tighter leading-none">Find the support you're looking for.</h1>
            <p className="text-white/85 text-lg mt-6 max-w-xl leading-relaxed">Massage, energy work, breathwork, coaching and more. Choose an offering, then pick a center, a practitioner and a time that works for you.</p>
            <div className="flex flex-wrap items-center gap-2 mt-8">
              {flowSteps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 bg-white/12 backdrop-blur rounded-full pl-2.5 pr-4 py-1.5 text-sm text-white"><Icon size={14} className="text-[#D98E73]" /> {s.label}</span>
                    {i < flowSteps.length - 1 && <ArrowRight size={14} className="text-white/50" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-20">
        {/* Matched-needs banner — carries homepage selections into results */}
        {needs.length > 0 && (
          <div className="flex flex-wrap items-center gap-2.5 bg-[#F5E6E0] border border-[#D98E73]/40 rounded-2xl px-5 py-4 mb-6" data-testid="wellness-needs-banner">
            <Star size={16} className="text-[#D98E73] shrink-0" />
            <span className="text-sm font-medium text-[#1A242B]">Matched to what you selected:</span>
            {needs.map((id) => (
              <button key={id} onClick={() => removeNeed(id)} data-testid={`needs-chip-${id}`}
                className="inline-flex items-center gap-1.5 bg-white border border-[#D98E73]/50 text-[#1A242B] rounded-full pl-3.5 pr-2.5 py-1.5 text-sm hover:border-[#D98E73] transition-colors">
                {NEED_THEMES[id].label} <span className="text-[#D98E73] font-semibold">×</span>
              </button>
            ))}
            <button onClick={clearNeeds} data-testid="needs-clear-all" className="text-xs font-medium text-[#4A5D6B] hover:text-[#144F75] underline underline-offset-2 ml-1">
              Show everything
            </button>
          </div>
        )}

        {/* Filter panel — a self-contained filter widget, not inline questions */}
        <div className="bg-white rounded-2xl border border-[#144F75]/12 shadow-sm mb-10 overflow-hidden" data-testid="wellness-filter-panel">
          <div className="flex items-center justify-between px-5 md:px-6 py-3.5 border-b border-[#144F75]/8 bg-[#144F75]/[0.03]">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#144F75]"><SlidersHorizontal size={16} /> Find your offering</span>
            {anyFilter && (
              <button onClick={() => { setLoc("all"); setEnh("all"); setCat("all"); setFocus("all"); setQ(""); }} data-testid="wellness-clear-filters" className="text-xs font-medium text-[#4A5D6B] hover:text-[#144F75] underline underline-offset-2">Clear all</button>
            )}
          </div>
          <div className="p-5 md:p-6 space-y-5">
            <div className="relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)} data-testid="wellness-search"
                placeholder="Search by name, keyword, or how you'd like support…"
                className="w-full rounded-xl border border-[#144F75]/15 bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#144F75]"
              />
            </div>
            {facets.focus_areas && facets.focus_areas.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Area of support</p>
                <div className="flex flex-wrap gap-2" data-testid="wellness-focus-filters">
                  <Chip active={focus === "all"} onClick={() => setFocus("all")} testid="wellness-focus-all">All areas</Chip>
                  {facets.focus_areas.map((f) => (
                    <Chip key={f} active={focus === f} onClick={() => setFocus(f)} testid={`wellness-focus-${f.toLowerCase().replace(/\s+/g, "-")}`}>{f}</Chip>
                  ))}
                </div>
                <p className="text-[11px] text-[#8FA89B] mt-2">Areas of support — not medical diagnosis or guaranteed treatment.</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Location</p>
              <div className="flex flex-wrap gap-2" data-testid="wellness-loc-filters">
                <Chip active={loc === "all"} onClick={() => setLoc("all")} testid="wellness-loc-all">All locations</Chip>
                {facets.locations.map((l) => (
                  <Chip key={l.id} active={loc === l.id} onClick={() => setLoc(l.id)} testid={`wellness-loc-${l.slug || l.id}`}>{l.name}</Chip>
                ))}
              </div>
            </div>
            {facets.enhancers.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Enhancer</p>
                <div className="flex flex-wrap gap-2" data-testid="wellness-enh-filters">
                  <Chip active={enh === "all"} onClick={() => setEnh("all")} testid="wellness-enh-all">All enhancers</Chip>
                  {facets.enhancers.map((e) => (
                    <Chip key={e.id} active={enh === e.id} onClick={() => setEnh(e.id)} testid={`wellness-enh-${e.id}`}>{e.name}</Chip>
                  ))}
                </div>
              </div>
            )}
            {facets.categories.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2">Category</p>
                <div className="flex flex-wrap gap-2" data-testid="wellness-cat-filters">
                  <Chip active={cat === "all"} onClick={() => setCat("all")} testid="wellness-cat-all">All</Chip>
                  {facets.categories.map((c) => (
                    <Chip key={c} active={cat === c} onClick={() => setCat(c)} testid={`wellness-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</Chip>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <p className="text-[#4A5D6B]">Loading offerings…</p>
        ) : (
          <>
          {anyFilter && <p className="text-sm text-[#4A5D6B] mb-5" data-testid="wellness-result-count">{resultCount} offering{resultCount === 1 ? "" : "s"} match your search.</p>}
          {groups ? (
            groups.length === 0 ? <EmptyState /> : (
              <div className="space-y-14">
                {groups.map(({ location, list }) => (
                  <div key={location.id} data-testid={`wellness-group-${location.slug || location.id}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <h2 className="font-serif text-3xl text-[#1A242B] tracking-tight">{location.name}</h2>
                      <LocationBadge kind={location.kind} name={location.kind === "flagship" ? "EYW Flagship" : location.kind === "virtual" ? "Online" : "Affiliate"} />
                      {location.id === homeId && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#5C8A72] bg-[#5C8A72]/10 rounded-full px-2.5 py-1" data-testid="wellness-home-center-badge"><Home size={12} /> Your home center</span>}
                    </div>
                    <Grid list={list} isMember={isMember} onBook={setSelected} />
                  </div>
                ))}
              </div>
            )
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <Grid list={filtered} isMember={isMember} onBook={setSelected} />
          )}
          </>
        )}

        {packages.length > 0 && (
          <div className="mt-16" data-testid="wellness-packages">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-serif text-3xl text-[#1A242B] tracking-tight">Bundle &amp; save with a package</h2>
                <p className="text-[#4A5D6B] mt-2">Prepay for multiple sessions and choose your offerings whenever you're ready.</p>
              </div>
              <Link to="/packages" className="eyw-btn-ghost hidden sm:inline-flex">All packages <ArrowRight size={16} /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {packages.map((p) => (
                <Link key={p.id} to="/packages" data-testid={`wellness-package-${p.id}`} className="eyw-card p-6 block hover:-translate-y-0.5 transition-transform">
                  <h3 className="font-serif text-xl text-[#1A242B]">{p.name}</h3>
                  {p.owner_type === "enhancer" && (
                    <p className="text-xs text-[#5C8A72] mt-1" data-testid={`wellness-package-owner-${p.id}`}>
                      by {p.owner_name || "Enhancer"}{p.centers?.length ? ` · at ${p.centers.map((c) => c.name).join(", ")}` : ""}
                    </p>
                  )}
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-3xl font-serif text-[#144F75]">{money(Number(p.price))}</span>
                    <span className="text-sm text-[#8FA89B]">· {p.sessions} sessions</span>
                  </div>
                  <ListingDescription text={p.description} item={p} kind="package" className="text-sm text-[#4A5D6B] mt-3 line-clamp-3" testid={`package-desc-${p.id}`} />
                  <span className="text-sm text-[#144F75] font-medium inline-flex items-center gap-1 mt-4">Choose this pathway <ArrowRight size={14} /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 rounded-3xl bg-[#144F75] p-10 lg:p-14 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl text-white tracking-tight">Members save on every offering.</h2>
          <p className="text-white/80 mt-3 max-w-2xl mx-auto">Members get lower prices, included community circles and early access to popular sessions.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to="/memberships" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">View Memberships</Link>
            <Link to="/gauge/app" className="eyw-btn-ghost !border-white !text-white hover:!bg-white hover:!text-[#144F75]">Not sure? Start with GAUGE</Link>
          </div>
        </div>
      </section>

      {selected ? (
        <Seo title={selected.title} description={stripHtml(selected.short_description || selected.description || "") || `Book ${selected.title} at Enhancing Your World.`}
          image={selected.image} path={`/offerings/${selected.slug || selected.id}`}
          jsonLd={{
            "@context": "https://schema.org", "@type": "Service", name: selected.title,
            serviceType: selected.category || undefined,
            description: stripHtml(selected.short_description || selected.description || "", 300) || undefined,
            image: selected.image || undefined,
            url: `${window.location.origin}/offerings/${selected.slug || selected.id}`,
            provider: { "@type": "Organization", name: "Enhancing Your World" },
            areaServed: (selected.centers || []).map((c) => c.name).filter(Boolean),
            offers: selected.price ? { "@type": "Offer", price: selected.price, priceCurrency: "USD" } : undefined,
          }} />
      ) : (
        <Seo title="Offerings & Services" description="Holistic wellness services, sessions and healing offerings across the Enhancing Your World centers." path="/offerings" />
      )}
      {selected && <BookingModal service={offerings.find((x) => x.id === selected.id) || selected} initialLocationId={loc !== "all" ? loc : ""} onClose={closeModal} />}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-14 rounded-2xl border border-dashed border-[#144F75]/20 bg-white" data-testid="wellness-empty">
      <p className="font-serif text-2xl text-[#1A242B]">No offerings match yet.</p>
      <p className="text-[#4A5D6B] mt-2 max-w-md mx-auto">Try removing a filter or using a broader word. Still not sure? Take the free GAUGE exploration, or call us and we'll help you find a good fit.</p>
      <div className="flex flex-wrap justify-center gap-3 mt-6">
        <Link to="/gauge/app" className="eyw-btn !py-2.5 text-sm">Start with GAUGE</Link>
        <a href="tel:+18599795505" className="eyw-btn-ghost !py-2.5 text-sm">Call 859-979-5505</a>
      </div>
    </div>
  );
}

function Grid({ list, isMember, onBook }) {
  const nav = useNavigate();
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {list.map((s, i) => {
        const discountedPrice = s.member_discount_pct > 0 ? (s.price * (1 - s.member_discount_pct / 100)).toFixed(2) : null;
        const memberPrice = isMember ? discountedPrice : null;
        const enhNames = (s.enhancers || []).map((e) => e.name).filter(Boolean);
        return (
          <div key={s.id} data-testid={`service-card-${i}`} className="group bg-white rounded-2xl border border-[#144F75]/10 shadow-sm overflow-hidden flex flex-col transition-transform hover:-translate-y-1 hover:shadow-md" onClick={() => { nav(`/offerings/${s.slug || s.id}`); onBook(s); }}>
            <div className="h-44 overflow-hidden relative">
              <img src={s.image || FALLBACK} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <span className="text-[11px] text-[#8FA89B] uppercase tracking-wider font-semibold">{s.category}</span>
              <h3 className="font-serif text-2xl text-[#1A242B] mt-1 leading-snug">{s.title}</h3>
              {s.access_level && s.access_level !== "public" && (
                <span data-testid={`service-access-badge-${i}`} className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide mt-1 px-2 py-0.5 rounded-full bg-[#144F75]/[0.08] text-[#144F75]"><Lock size={9} /> Member access required</span>
              )}
              {/* Location + virtual badges */}
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`service-badges-${i}`}>
                {(s.centers || []).map((c) => <LocationBadge key={c.id} kind={c.kind} name={c.kind === "flagship" ? "EYW Flagship" : "Affiliate"} />)}
                {s.virtual && <LocationBadge kind="virtual" name="Virtual" />}
              </div>
              <ListingDescription text={s.short_description || s.description} item={s} className="text-[#4A5D6B] text-sm leading-relaxed mt-2 flex-1 line-clamp-3" testid={`service-desc-${i}`} />
              {enhNames.length > 0 && (
                <p className="text-xs text-[#4A5D6B] mt-3 flex items-center gap-1.5"><Star size={12} className="text-[#D98E73]" /> {enhNames.length > 2 ? `${enhNames.slice(0, 2).join(", ")} +${enhNames.length - 2}` : enhNames.join(", ")}</p>
              )}
              <div className="flex items-center gap-4 text-xs text-[#4A5D6B] mt-3">
                <span className="flex items-center gap-1"><Clock size={13} /> {s.duration_min} min</span>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div>
                  <div className="font-serif text-2xl text-[#144F75]">
                    {s.price > 0 ? `${money(memberPrice || s.price)}` : "Free"}
                    {memberPrice && <span className="text-xs text-[#8FA89B] ml-2 line-through">{money(s.price)}</span>}
                  </div>
                  {!memberPrice && discountedPrice && (
                    <div className="text-xs text-[#5C8A72] mt-0.5">Members: {money(discountedPrice)}</div>
                  )}
                </div>
                <button onClick={() => onBook(s)} data-testid={`service-book-${i}`} className="eyw-btn !px-5 !py-2 text-sm">Book <ArrowRight size={15} /></button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
